// Dr.Rx™ Sign-In & Hidden SEO/GEO Knowledge Layer
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SEO from './SEO';

const AnimatedLogoIcon = () => (
    <svg width="96" height="96" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-6">
        <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        
        {/* Rotating outer structure */}
        <g style={{ transformOrigin: '24px 24px', animation: 'spin-slow 25s linear infinite' }}>
            {/* Main Axes */}
            <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2" strokeOpacity="0.8"/>
            <circle cx="24" cy="7" r="2" fill="white"/>
            <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2" strokeOpacity="0.8"/>
            <circle cx="24" cy="41" r="2" fill="white"/>
            <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2" strokeOpacity="0.8"/>
            <circle cx="7" cy="24" r="2" fill="white"/>
            <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2" strokeOpacity="0.8"/>
            <circle cx="41" cy="24" r="2" fill="white"/>
            
            {/* Diagonal Axes */}
            <line x1="13.39" y1="13.39" x2="19.05" y2="19.05" stroke="white" strokeWidth="2" strokeOpacity="0.6"/>
            <circle cx="11.98" cy="11.98" r="2" fill="white" fillOpacity="0.8"/>
            <line x1="28.95" y1="28.95" x2="34.61" y2="34.61" stroke="white" strokeWidth="2" strokeOpacity="0.6"/>
            <circle cx="36.02" cy="36.02" r="2" fill="white" fillOpacity="0.8"/>
            <line x1="34.61" y1="13.39" x2="28.95" y2="19.05" stroke="white" strokeWidth="2" strokeOpacity="0.6"/>
            <circle cx="36.02" cy="11.98" r="2" fill="white" fillOpacity="0.8"/>
            <line x1="19.05" y1="28.95" x2="13.39" y2="34.61" stroke="white" strokeWidth="2" strokeOpacity="0.6"/>
            <circle cx="11.98" cy="36.02" r="2" fill="white" fillOpacity="0.8"/>
        </g>

        {/* Pulsing Core */}
        <circle cx="24" cy="24" r="5" fill="#FACC15" filter="url(#glow)" style={{ transformOrigin: '24px 24px', animation: 'pulse-core 3s ease-in-out infinite' }}/>
    </svg>
);

const GoogleIcon = () => (
    <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.657-3.356-11.303-7.962l-6.571,4.819C9.656,39.663,16.318,44,24,44z"></path>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C42.012,35.245,44,30.028,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
    </svg>
);

const LOGIN_SEO_SCHEMA = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": ["SoftwareApplication", "MedicalWebPage"],
            "@id": "https://drrx.app/#application",
            "name": "Dr.Rx™ - AI Clinical Pharmacist & Pharmacy Decision Support Co-Pilot",
            "alternateName": ["Dr.Rx", "Dr.Rx App", "Dr.Rx Clinical Co-Pilot", "NV Pharmaceutics Dr.Rx"],
            "url": "https://drrx.app/",
            "applicationCategory": "MedicalApplication",
            "medicalSpecialty": ["Pharmacy", "ClinicalPharmacology", "InternalMedicine", "CriticalCare", "InfectiousDisease", "EmergencyMedicine", "Nursing"],
            "operatingSystem": "Web, iOS, Android, macOS, Windows (PWA)",
            "description": "Dr.Rx™ is an evidence-based AI Clinical Pharmacist Co-Pilot and point-of-care clinical decision support platform for pharmacists, physicians, nurses, and PharmD students. Includes 40+ clinical calculators (CrCl renal dosing, Vancomycin AUC/PK, Y-site IV compatibility, Opioid MME converter, Anticoagulation bridging, Beers Criteria, Pediatric mg/kg dosing), real-time FDA/CDC/DEA regulatory alerts, Top 200 Drug monographs, and clinical practice guidelines.",
            "featureList": [
                "AI Clinical Pharmacist Chat Co-Pilot with Pharmacist, Physician, Nurse, and Researcher calibration",
                "Renal & Hepatic Dose Adjustment Calculator (Cockcroft-Gault CrCl & Child-Pugh)",
                "IV Y-Site Compatibility & Reconstitution Stability Checker",
                "Multi-Drug Interaction (DDI) & CYP450/P-gp Pathway Analyzer",
                "Vancomycin AUC/MIC & Aminoglycoside Pharmacokinetics Calculator",
                "Perioperative Anticoagulation Bridging & DOAC Switching Tool",
                "Opioid Morphine Milligram Equivalent (MME) & Equianalgesic Dosing Calculator",
                "Insulin Conversion, Basal-Bolus & Sliding Scale Dosing Tool",
                "Antimicrobial Stewardship, Bug-Drug Coverage & Empiric Antibiotic Guide",
                "AGS Beers Criteria Geriatric Potentially Inappropriate Medication Checker",
                "Anticholinergic Cognitive Burden (ACB) Score Calculator",
                "FDA Pregnancy & Lactation (LactMed) Medication Safety Reference",
                "Pediatric Weight-Based Dosing (mg/kg) & Suspension Calculator",
                "USP <795> / <797> / <800> Compounding Calculations & Master Formulas",
                "Federal & State MPJE Pharmacy Law Reference & Controlled Substance (DEA Schedule II-V) Guide",
                "Top 200 Drugs Flashcards, NAPLEX / MPJE Quiz & Clinical Pearls",
                "Real-time FDA Drug Recalls, Black Box Warnings, Drug Shortages & CDC Health Advisories"
            ],
            "offers": {
                "@type": "Offer",
                "price": "0.00",
                "priceCurrency": "USD",
                "description": "Free tier with daily AI clinical consultations, full Drug Index access, national FDA/CDC/DEA alerts, and core clinical calculators."
            },
            "author": {
                "@type": "Organization",
                "name": "NV Pharmaceutics LLC",
                "url": "https://drrx.app/"
            }
        },
        {
            "@type": "FAQPage",
            "@id": "https://drrx.app/#faq",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What is Dr.Rx™ AI Pharmacy Co-Pilot?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dr.Rx™ is an evidence-based AI Clinical Pharmacist and clinical decision support (CDS) web application engineered by NV Pharmaceutics LLC for pharmacists, physicians, nurses, clinical researchers, and pharmacy students. It combines an AI clinical consultation engine with 40+ point-of-care calculators, Top 100/200 drug monographs, and live FDA/CDC/DEA regulatory alerts."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Which clinical pharmacy calculators are included in Dr.Rx™?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dr.Rx™ includes over 40 specialized clinical tools: Creatinine Clearance (CrCl) and Renal/Hepatic Dose Adjustment, Y-Site IV Compatibility, Drug-Drug Interaction (DDI) Checker, Vancomycin & Aminoglycoside Pharmacokinetics, Opioid MME Calculator, Anticoagulation Bridging, Insulin Conversion, Pediatric Weight-Based Dosing, Beers Criteria, Anticholinergic Burden, MTM/CMR Generator, Prior Authorization (PA) Assistant, and USP Compounding Calculators."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How does Dr.Rx™ support NAPLEX, MPJE, and clinical rotations?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dr.Rx™ provides an interactive Top 200 Drugs Study Mode with flashcards, custom clinical quizzes, pharmacy mnemonics, biostatistics calculators (NNT, NNH, ARR, RRR), and Federal/50-State Pharmacy Law references tailored for PharmD students, pharmacy residents, and practicing clinicians."
                    }
                }
            ]
        }
    ]
};

const Login: React.FC = () => {
    const { loginWithGoogle } = useAuth();
    const [isExiting, setIsExiting] = useState(false);

    const handleSignInClick = () => {
        if (isExiting) return;
        setIsExiting(true);

        // Wait for the peel-off and flash animation to mostly complete before triggering the popup
        setTimeout(async () => {
            try {
                await loginWithGoogle();
                // If successful, AuthContext will unmount this component automatically.
            } catch (e) {
                // If user closes popup or an error occurs, reset the animation
                setIsExiting(false);
            }
        }, 700);
    };

    return (
        <div className="min-h-screen bg-[#101D35] text-gray-200 flex flex-col items-center justify-center p-4 antialiased relative overflow-hidden">
            <SEO
                title="Dr.Rx™ - AI Clinical Pharmacist Co-Pilot, Drug Reference & 40+ Pharmacy Calculators"
                description="Evidence-based AI Clinical Pharmacist Co-Pilot for pharmacists, physicians, nurses, and PharmD students. Access renal dosing, IV Y-site compatibility, drug interactions, pharmacokinetics, FDA/CDC/DEA alerts, and 40+ clinical calculators."
                keywords={[
                    "AI Clinical Pharmacist",
                    "Pharmacy Clinical Decision Support",
                    "Drug Interaction Checker",
                    "Renal Dose Calculator",
                    "IV Y-Site Compatibility",
                    "Vancomycin AUC Calculator",
                    "Opioid MME Calculator",
                    "Anticoagulation Bridging Tool",
                    "Top 200 Drugs Study Mode",
                    "NAPLEX MPJE Prep",
                    "FDA Drug Recalls",
                    "Dr.Rx",
                    "NV Pharmaceutics"
                ]}
                schema={LOGIN_SEO_SCHEMA}
            />

            {/* White flash overlay */}
            <div className={`fixed inset-0 bg-white pointer-events-none z-50 ${isExiting ? 'flash-active' : 'opacity-0'}`} />

            <div className={`text-center max-w-md w-full z-10 ${isExiting ? 'peel-out' : ''}`}>
                <AnimatedLogoIcon />
                <h1 className="text-6xl font-bold text-white">Dr.<span className="text-yellow-400">Rx</span><sup className="text-3xl -top-6">TM</sup></h1>
                <p className="text-xl text-gray-400 mt-2">Your AI Pharmacy Co-Pilot</p>

                <div className="mt-12 space-y-4">
                    <button
                        onClick={handleSignInClick}
                        className="w-full max-w-xs mx-auto flex items-center justify-center bg-white text-gray-800 font-semibold py-3 px-6 rounded-lg shadow-lg hover:bg-gray-200 transition-transform hover:scale-105 peel-in"
                        style={{ animationDelay: '0.2s' }}
                    >
                        <GoogleIcon />
                        Sign in with Google
                    </button>
                </div>
            </div>

            {/* Hidden Semantic SEO & AI Search (GEO/AEO) Knowledge Layer — Zero Visual Impact */}
            <section className="sr-only" aria-label="Dr.Rx Clinical Decision Support Platform Directory">
                <h2>Dr.Rx™ AI Clinical Pharmacist & Point-of-Care Decision Support Suite by NV Pharmaceutics LLC</h2>
                <p>
                    Dr.Rx™ is an advanced clinical decision support (CDS) platform engineered for licensed pharmacists, physicians, nurse practitioners, physician assistants, critical care nurses, clinical researchers, and PharmD students. Dr.Rx™ delivers zero-fluff, guideline-concordant pharmacology answers grounded in FDA prescribing information, CDC advisories, USP standards, and ACC/AHA, IDSA, ADA, CHEST, KDIGO, and AGS Beers Criteria guidelines.
                </p>
                <h3>Core Clinical Capabilities & 40+ Point-of-Care Pharmacy Calculators</h3>
                <ul>
                    <li>AI Clinical Pharmacist Consultation Chat: Role-calibrated clinical answers for Pharmacists, Physicians, Nurses, and Researchers in Short (&lt;150 words) or Detailed Monograph modes.</li>
                    <li>Renal & Hepatic Dose Adjustment Engine: Cockcroft-Gault Creatinine Clearance (CrCl), eGFR, hemodialysis supplemental dosing, and Child-Pugh hepatic titration.</li>
                    <li>IV Y-Site Compatibility & Stability: Concentration-dependent Y-site compatibility, reconstitution diluents, and infusion rates for critical care medications.</li>
                    <li>Drug-Drug Interaction (DDI) & Pharmacogenomics (PGx) Checker: CYP450, P-glycoprotein (P-gp), QTc prolongation, serotonin syndrome, and CPIC genotype dosing guidance.</li>
                    <li>Clinical Pharmacokinetics & TDM: Vancomycin AUC/MIC Bayesian-style dosing, aminoglycosides, phenytoin winter-tozer correction, and warfarin INR management.</li>
                    <li>Perioperative Anticoagulation Bridging & DOAC Transitions: Hold timelines and bridging protocols for apixaban, rivaroxaban, dabigatran, edoxaban, and warfarin.</li>
                    <li>Opioid MME & Corticosteroid Equianalgesic Converters: CDC Morphine Milligram Equivalent calculations, cross-tapering, and glucocorticoid potency conversion.</li>
                    <li>Infectious Disease & Antimicrobial Stewardship: Empiric antibiotic selection, bug-drug spectrum, HIV antiretroviral regimens, and CDC ACIP vaccine schedules.</li>
                    <li>Special Populations: Pediatric mg/kg weight-based dosing, AGS Beers Criteria geriatric safety, Anticholinergic Burden (ACB), and FDA Pregnancy & Lactation (LactMed) safety.</li>
                    <li>Pharmacy Operations, Compounding & Law: USP &lt;795&gt;/&lt;797&gt;/&lt;800&gt; compounding calculations, Federal & 50-State MPJE pharmacy law, DEA Controlled Substance reference, Prior Authorization (PA) letter generator, MTM/CMR action plans, and FDA Drug Shortage alternatives.</li>
                </ul>
            </section>

            <footer className={`absolute bottom-6 text-center text-gray-600 text-xs z-10 transition-opacity duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
                <p>&copy;2025 NV Pharmaceutics LLC. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default Login;